import * as React from 'react';
import * as ResizablePrimitive from 'react-resizable-panels';
import { cn } from '@utils/cn';

/**
 * Resizable Panel Group
 *
 * Container for resizable panels. Manages the layout and sizing of child panels.
 *
 * @example
 * ```tsx
 * <ResizablePanelGroup direction="horizontal">
 *   <ResizablePanel defaultSize={50}>
 *     <div>Panel 1</div>
 *   </ResizablePanel>
 *   <ResizableHandle />
 *   <ResizablePanel defaultSize={50}>
 *     <div>Panel 2</div>
 *   </ResizablePanel>
 * </ResizablePanelGroup>
 * ```
 */
const ResizablePanelGroup = React.forwardRef<
  React.ElementRef<typeof ResizablePrimitive.PanelGroup>,
  React.ComponentPropsWithoutRef<typeof ResizablePrimitive.PanelGroup>
>(({ className, ...props }, ref) => (
  <ResizablePrimitive.PanelGroup
    ref={ref}
    className={cn('flex h-full w-full data-[panel-group-direction=vertical]:flex-col', className)}
    {...props}
  />
));
ResizablePanelGroup.displayName = 'ResizablePanelGroup';

/**
 * Resizable Panel
 *
 * Individual panel within a ResizablePanelGroup. Can be resized by dragging handles.
 *
 * @example
 * ```tsx
 * <ResizablePanel defaultSize={50} minSize={20} maxSize={80}>
 *   <div>Content</div>
 * </ResizablePanel>
 * ```
 */
const ResizablePanel = React.forwardRef<
  React.ElementRef<typeof ResizablePrimitive.Panel>,
  React.ComponentPropsWithoutRef<typeof ResizablePrimitive.Panel>
>(({ className, ...props }, ref) => (
  <ResizablePrimitive.Panel ref={ref} className={cn('relative', className)} {...props} />
));
ResizablePanel.displayName = 'ResizablePanel';

/**
 * Resizable Handle
 *
 * Interactive handle for resizing panels. Appears between panels in a ResizablePanelGroup.
 * Styled to match the Pranik design system with consistent colors and hover states.
 *
 * @example
 * ```tsx
 * <ResizableHandle />
 * <ResizableHandle withHandle /> // Shows visible drag indicator
 * ```
 */
const ResizableHandle = ({
  className,
  withHandle,
  ...props
}: React.ComponentPropsWithoutRef<typeof ResizablePrimitive.PanelResizeHandle> & {
  withHandle?: boolean;
}) => (
  <ResizablePrimitive.PanelResizeHandle
    className={cn(
      'relative flex w-px items-center justify-center bg-border after:absolute after:inset-y-0 after:left-1/2 after:w-1 after:-translate-x-1/2 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1 data-[panel-group-direction=vertical]:h-px data-[panel-group-direction=vertical]:w-full data-[panel-group-direction=vertical]:after:left-0 data-[panel-group-direction=vertical]:after:h-1 data-[panel-group-direction=vertical]:after:w-full data-[panel-group-direction=vertical]:after:-translate-y-1/2 data-[panel-group-direction=vertical]:after:translate-x-0 [&[data-panel-group-direction=vertical]>div]:rotate-90',
      className
    )}
    {...props}
  >
    {withHandle && (
      <div className="z-10 flex h-4 w-3 items-center justify-center rounded-sm border border-border bg-border transition-colors hover:bg-gray-300">
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="text-gray-500"
        >
          <circle cx="4" cy="4" r="0.75" fill="currentColor" />
          <circle cx="4" cy="8" r="0.75" fill="currentColor" />
          <circle cx="8" cy="4" r="0.75" fill="currentColor" />
          <circle cx="8" cy="8" r="0.75" fill="currentColor" />
        </svg>
      </div>
    )}
  </ResizablePrimitive.PanelResizeHandle>
);
ResizableHandle.displayName = 'ResizableHandle';

export { ResizablePanelGroup, ResizablePanel, ResizableHandle };
